import { useMemo } from 'react'
import { View, Text, Pressable, Platform } from 'react-native'
import {
  Sparkles,
  Palette,
  Users,
  Settings,
  Package,
  CreditCard,
  Lock,
} from 'lucide-react-native'
import type { LucideIcon } from 'lucide-react-native'
import { useTheme } from '../../../theme/useTheme'
import { WEB_HOVER_TRANSITION_STYLE } from '../../../shared/constants/animations'
import {
  ACCOUNT_ADMIN_NAV,
  ACCOUNT_ADMIN_NAV_ITEMS,
  type AccountAdminNavId,
} from '../../constants/accountAdmin.constants'
import { getAccountAdminNavLabel } from '../../helpers/accountAdminNav.helper'
import { useAccountAdminCopy } from '../../hooks/useAccountAdminCopy'
import { getAccountAdminSidebarStyles } from './AccountAdminSidebar.styles'

const NAV_ICONS: Record<AccountAdminNavId, LucideIcon> = {
  [ACCOUNT_ADMIN_NAV.START_HERE]: Sparkles,
  [ACCOUNT_ADMIN_NAV.BRANDING]: Palette,
  [ACCOUNT_ADMIN_NAV.MEMBERS]: Users,
  [ACCOUNT_ADMIN_NAV.SETTINGS]: Settings,
  [ACCOUNT_ADMIN_NAV.PLAN_BILLING]: Package,
  [ACCOUNT_ADMIN_NAV.INVOICE_DETAILS]: CreditCard,
}

const BILLING_LOCKED_NAV_IDS: ReadonlySet<AccountAdminNavId> = new Set([
  ACCOUNT_ADMIN_NAV.BRANDING,
  ACCOUNT_ADMIN_NAV.MEMBERS,
  ACCOUNT_ADMIN_NAV.SETTINGS,
])

const ICON_SIZE = 20
const ICON_STROKE = 2.25
const LOCK_SIZE = 14

interface AccountAdminSidebarProps {
  activeNav: AccountAdminNavId
  onSelectNav: (id: AccountAdminNavId) => void
  /** When false, Branding / Members / Settings show a lock and route to Plan & Billing. */
  billingUnlocked?: boolean
}

export function AccountAdminSidebar({
  activeNav,
  onSelectNav,
  billingUnlocked = true,
}: AccountAdminSidebarProps) {
  const theme = useTheme()
  const copy = useAccountAdminCopy()
  const styles = useMemo(() => getAccountAdminSidebarStyles(theme), [theme])

  return (
    <View style={styles.sidebar}>
      {ACCOUNT_ADMIN_NAV_ITEMS.map((id) => {
        const isLocked = !billingUnlocked && BILLING_LOCKED_NAV_IDS.has(id)
        const selected = activeNav === id
        const Icon = NAV_ICONS[id]
        const label = getAccountAdminNavLabel(id, copy)
        const handlePress = () =>
          isLocked ? onSelectNav(ACCOUNT_ADMIN_NAV.PLAN_BILLING) : onSelectNav(id)
        return (
          <Pressable
            key={id}
            accessibilityRole="button"
            accessibilityState={{ selected }}
            accessibilityLabel={isLocked ? `${label}${copy.navLockedA11ySuffix}` : label}
            onPress={handlePress}
            style={({ pressed, hovered }) => [
              styles.navRow,
              selected && styles.navRowActive,
              isLocked && styles.navRowLocked,
              Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              pressed && styles.navRowPressed,
              hovered && Platform.OS === 'web' && styles.navRowHover,
            ]}
          >
            <Icon
              size={ICON_SIZE}
              color={isLocked ? theme.gray[400] : selected ? theme.primaryLight : theme.gray[600]}
              strokeWidth={ICON_STROKE}
            />
            <Text
              style={[
                styles.navLabel,
                selected && styles.navLabelActive,
                isLocked && styles.navLabelLocked,
              ]}
            >
              {label}
            </Text>
            {isLocked ? (
              <Lock size={LOCK_SIZE} color={theme.gray[400]} strokeWidth={2} />
            ) : null}
          </Pressable>
        )
      })}
    </View>
  )
}
